import React, { useState, FC } from 'react';
import styled from 'styled-components';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Esi } from '@empiriecom/module-components/Esi';
import { Link } from '@empiriecom/module-components/Link';
import { Modal } from '@empiriecom/module-components/Modal';
import { AttributeBlock, Attribute, AttributeValueObject } from '@/interfaces';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import useUrlParameters from '../../hooks/useUrlParameters';

export type DeliveryMessageProps = {
  deliveryAttribute: AttributeBlock | undefined;
};

const DeliveryText = styled.div`
  text-align: center;
  font-size: 0.8125rem;
  line-height: 1.5rem;
  margin-bottom: 0.5rem;
`;

const ModalLink = styled(Link)`
  text-decoration: underline;
  color: ${({ theme }) => theme.base.color.text};
  background: none;
  border: 0px none;
  box-shadow: 0px none;
  padding: 0;
  margin: 0;
  display: inline;
  font-size: inherit;
  &&:hover {
    color: ${({ theme }) => theme.greyDark.color.normal};
    border: 0px none;
  }
`;

const messages = defineMessages({
  modalHeadline: {
    defaultMessage: 'Speditionsversand',
    id: 'availabilityMessage.modalHeadline',
  },
  speditionAddition: {
    defaultMessage:
      'Artikel wird <modal>bis zur Grundstücksgrenze</modal> geliefert (nur bei LKW-befahrbarer Straße)',
    description: 'hint text for carrier delivery to location',
    id: 'availabilityMessage.speditionAddition',
  },
  speditionHint: {
    defaultMessage: 'wird per <modal>Spedition</modal> geliefert',
    description: 'hint text for carrier delivery',
    id: 'availabilityMessage.speditionHint',
  },
});

const MessageElement: FC<{ deliveryMessage: Attribute }> = ({
  deliveryMessage: { type, values = [] },
}) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const intl = useIntl();
  const dispatchGTMEvent = useTracking();
  // return null if type is not recognized
  if (!['speditionAddition', 'speditionHint'].includes(type)) {
    return null;
  }

  const [{ link }] = values as AttributeValueObject[];
  const esiURL = useUrlParameters(link as string);
  const messageValues = {
    modal: (msg: string) => (
      <ModalLink
        data-testid="delivery-carrier-modal-link"
        onClick={() => {
          if (typeof esiURL === 'string') {
            setModalOpen(true);
            dispatchGTMEvent({
              event: 'ga_event',
              eventAction: 'viewLayer',
              eventCategory: 'noteLayer',
              eventLabel: 'detailview',
              eventValue: intl.formatMessage(messages.modalHeadline).concat(' ', type),
            });
          }
        }}
        renderAs="span"
        to=""
      >
        {msg}
      </ModalLink>
    ),
  };

  return (
    <>
      <DeliveryText data-testid="delivery-carrier-message">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormattedMessage {...messages[type]} values={messageValues} />
      </DeliveryText>
      {typeof esiURL === 'string' && (
        <Modal
          isOpen={isModalOpen}
          headline={intl.formatMessage(messages.modalHeadline)}
          onRequestClose={() => setModalOpen(false)}
        >
          <div data-testid="delivery-modal">
            <Esi src={esiURL as string} />
          </div>
        </Modal>
      )}
    </>
  );
};

/**
 * The `DeliveryMessage` component displays the hints for carrier delivery and delivery to location.
 * <p>The `intl` messages can contain a `<modal>`-link for display of a layer with content determined by the `deliveryType` prop.</p>
 */
export const DeliveryMessage: FC<DeliveryMessageProps> = ({ deliveryAttribute }) => {
  if (!deliveryAttribute) {
    return null;
  }

  return (
    <>
      {deliveryAttribute.values.map((deliveryMessage) => (
        <MessageElement deliveryMessage={deliveryMessage} key={deliveryMessage.type} />
      ))}
    </>
  );
};

export default DeliveryMessage;
